import _ from "lodash";

const PARENT_TYPE_CONFIG = {
  GRADE: { field: "grades", typename: "Grade" },
  SUBJECT: { field: "subjects", typename: "Subject" },
  SUBJECT_GROUP: { field: "subjectGroups", typename: "SubjectGroup" },
  PLANNER_ELEMENT_NODE: {
    field: "associatedPlannerElementNodes",
    typename: "PlannerElementNode",
  },
};

/**
 * Gets associated parents of a specific type from a planner element node.
 * Reads from the typed array under dataSetNodeWrapper.properties when present,
 * with fallback to legacy associatedParents union array.
 *
 * Consumers MUST ensure the fragment selects either the new typed arrays
 * (dataSetNodeWrapper.properties.{grades|subjects|subjectGroups|associatedPlannerElementNodes})
 * or the legacy associatedParents union. Selecting dataSetNodeWrapper.properties
 * without the typed array for `type` AND dropping associatedParents will silently
 * return [].
 */
export const getNodeParents = (node, type) => {
  const config = PARENT_TYPE_CONFIG[type];
  if (config) {
    const properties = _.get(node, "dataSetNodeWrapper.properties");
    if (properties) {
      if (
        process.env.NODE_ENV !== "production" &&
        !(config.field in properties)
      ) {
        // eslint-disable-next-line no-console
        console.warn(
          `[getNodeParents] dataSetNodeWrapper.properties is present but "${config.field}" was not selected for type "${type}". Fragment must select either the typed array or legacy associatedParents.`
        );
      }
      const data = properties[config.field];
      if (data !== undefined) return data || [];
    }
    return _.filter(_.get(node, "associatedParents", []), {
      __typename: config.typename,
    });
  }
  return _.get(node, "associatedParents", []);
};

/**
 * Returns all associated parents of a node across every type.
 * Delegates to getNodeParents per known type so detection logic stays in
 * one place. Items from the typed-array path do not carry __typename and
 * only include fields selected for that type — consumers must only rely on
 * fields common to all parent types (e.g. id).
 */
export const getAllNodeParents = node =>
  _.concat(
    getNodeParents(node, "GRADE"),
    getNodeParents(node, "SUBJECT"),
    getNodeParents(node, "SUBJECT_GROUP"),
    getNodeParents(node, "PLANNER_ELEMENT_NODE")
  );
